import { Controller } from "@hotwired/stimulus"

// Phase 3.8 Tier 4 Step 47 — Dropbox folder picker.
//
// Dropbox doesn't ship a hosted folder picker for backend OAuth flows
// (the Chooser is client-side only — meant for end-users picking files
// in their own browser, not for embedding in a server-side admin UI).
// We build our own click-to-expand left-rail tree against the
// /integrations/dropbox/folders endpoint, which proxies Dropbox's
// /2/files/list_folder.
//
// UX:
//   * Click "Pick folder" → modal opens
//   * Modal shows the connected account's root, lazy-loads children on
//     click. Breadcrumb at the top reflects current depth.
//   * Click a folder name to dive in; click "Use this folder" to pick
//     the current folder (you can pick the root by selecting it before
//     drilling in). Selection writes id + name + path_display into the
//     hidden form fields the linker controller reads on submit.
//
// Path strategy: Dropbox `external_id` for files is the immutable
// `id:abc123` form, but the linker form persists the **path** as
// `doc_sync.root_folder_id` because:
//   * Admins recognize paths in the linker UI (the modal renders
//     "Selected: /Apps/CustomerCo/Projects/Riverside")
//   * Dropbox accepts both `path` and `id:abc...` interchangeably on
//     /2/files/list_folder, so the engine's bootstrap walk works
//     identically with the path form
//   * Per-file external_ids in IntegrationExternalId still use the
//     `id:` form (set by the adapter on each entry's normalized event)
//     so renames don't collide
export default class extends Controller {
  static values = {
    listEndpoint:        String,
    connectionId:        String
  }

  static targets = [
    "display", "folderIdField", "folderNameField", "folderUrlField",
    "modal", "breadcrumb", "list", "currentSelection", "pickButton",
    "pickHint"
  ]

  connect() {
    this._currentPath = ""
    this._currentName = "All folders"
  }

  open(event) {
    event.preventDefault()
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    this._currentPath = ""
    this._currentName = "All folders"
    this._renderBreadcrumb()
    this._updatePickButton()
    this._loadFolder("")
  }

  close(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
  }

  // The "Use this folder" button — commits the currently-displayed
  // folder to the linker form.
  //
  // Root selection is intentionally disabled. DocSyncPollJob.perform
  // skips when `link.config["doc_sync.root_folder_id"]` is blank, so
  // syncing "" (Dropbox's root) would silently never run. A blank
  // selection is also nearly always the wrong sync intent — customers
  // typically want a project subfolder, not their entire Dropbox
  // mirrored. The button is disabled while at root and the hint
  // explains why.
  pick(event) {
    if (event) event.preventDefault()
    if (!this._currentPath || this._currentPath === "") {
      // Defense in depth — the button is also visually disabled when
      // at root, but a determined user could still trigger this via
      // keyboard or a stale event. Refuse silently rather than
      // committing a broken link.
      return
    }
    const id = this._currentPath
    const name = this._currentName || id.split("/").pop() || id

    if (this.hasFolderIdFieldTarget)   this.folderIdFieldTarget.value   = id
    if (this.hasFolderNameFieldTarget) this.folderNameFieldTarget.value = name
    if (this.hasFolderUrlFieldTarget)  this.folderUrlFieldTarget.value  = ""
    if (this.hasDisplayTarget)         this.displayTarget.value         = name
    this.close()
  }

  // Click on a folder row inside the list — drills into it.
  drillIn(event) {
    event.preventDefault()
    const path = event.currentTarget.dataset.path || ""
    const name = event.currentTarget.dataset.name || ""
    this._currentPath = path
    this._currentName = name
    this._renderBreadcrumb()
    this._updatePickButton()
    this._loadFolder(path)
  }

  // Click on a breadcrumb segment — pops back up the tree.
  popTo(event) {
    event.preventDefault()
    const path = event.currentTarget.dataset.path || ""
    const name = event.currentTarget.dataset.name ||
                 (path === "" ? "All folders" : path.split("/").pop() || path)
    this._currentPath = path
    this._currentName = name
    this._renderBreadcrumb()
    this._updatePickButton()
    this._loadFolder(path)
  }

  // Visually disable + hint at the "Use this folder" button when the
  // current path is the account root. The pick handler defends in
  // depth too.
  _updatePickButton() {
    const atRoot = !this._currentPath || this._currentPath === ""
    if (this.hasPickButtonTarget) {
      this.pickButtonTarget.disabled = atRoot
      if (atRoot) {
        this.pickButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.pickButtonTarget.classList.remove("hover:bg-brand-dark", "cursor-pointer")
      } else {
        this.pickButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.pickButtonTarget.classList.add("hover:bg-brand-dark", "cursor-pointer")
      }
    }
    if (this.hasPickHintTarget) {
      this.pickHintTarget.textContent = atRoot
        ? "Drill into a subfolder — sync needs a specific folder, not the entire account."
        : ""
    }
  }

  async _loadFolder(path) {
    if (!this.hasListTarget) return
    this._setListLoading()
    try {
      const params = new URLSearchParams({
        integration_connection_id: this.connectionIdValue,
        path: path
      })
      const url = `${this.listEndpointValue}?${params.toString()}`
      const res = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}))
        this._setListError(detail?.error?.message || `HTTP ${res.status}`)
        return
      }
      const json = await res.json()
      this._renderFolderList(json.entries || [])
    } catch (err) {
      this._setListError(err.message || String(err))
    }
  }

  _renderFolderList(entries) {
    if (!this.hasListTarget) return
    if (entries.length === 0) {
      this.listTarget.innerHTML = `
        <div class="text-center text-[12px] text-gray-500 py-6">
          No subfolders here. Click "Use this folder" to pick the current folder.
        </div>`
      return
    }
    const items = entries.map((entry) => {
      const safePath = this._escape(entry.path_display || entry.path_lower || "")
      const safeName = this._escape(entry.name || "")
      return `
        <button type="button"
                data-action="click->dropbox-folder-picker#drillIn"
                data-path="${safePath}"
                data-name="${safeName}"
                class="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-gray-50 rounded-lg cursor-pointer">
          <svg class="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/>
          </svg>
          <span class="flex-1 min-w-0 truncate">${safeName}</span>
          <svg class="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>`
    }).join("")
    this.listTarget.innerHTML = items
  }

  _renderBreadcrumb() {
    if (!this.hasBreadcrumbTarget) return
    const path = this._currentPath || ""
    const segments = path.split("/").filter((s) => s.length > 0)
    const crumbs = []
    crumbs.push(this._breadcrumbCrumb("", "All folders", segments.length === 0))
    let accum = ""
    segments.forEach((seg, idx) => {
      accum = `${accum}/${seg}`
      crumbs.push(this._breadcrumbCrumb(accum, seg, idx === segments.length - 1))
    })
    this.breadcrumbTarget.innerHTML = crumbs.join(`
      <svg class="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 18l6-6-6-6"/>
      </svg>`)
    if (this.hasCurrentSelectionTarget) {
      this.currentSelectionTarget.textContent = this._currentPath ? this._currentPath : "/ (account root)"
    }
  }

  _breadcrumbCrumb(path, name, current) {
    const safe = this._escape(name)
    const safePath = this._escape(path)
    const cls = current
      ? "text-gray-900 font-medium"
      : "text-blue-600 hover:underline cursor-pointer"
    return `<button type="button"
                    data-action="click->dropbox-folder-picker#popTo"
                    data-path="${safePath}"
                    data-name="${safe}"
                    class="${cls} text-[13px]">${safe}</button>`
  }

  _setListLoading() {
    if (!this.hasListTarget) return
    this.listTarget.innerHTML = `
      <div class="text-center text-[12px] text-gray-500 py-6">Loading folders…</div>`
  }

  _setListError(message) {
    if (!this.hasListTarget) return
    this.listTarget.innerHTML = `
      <div class="text-center text-[12px] text-rose-600 py-6">${this._escape(message)}</div>`
  }

  _escape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
